import {
    ADMIN_LOGIN_REQUEST,
    ADMIN_LOGIN_SUCCESS,
    ADMIN_LOGIN_FAILURE,
    VERIFY_MFA_REQUEST,
    VERIFY_MFA_SUCCESS,
    VERIFY_MFA_FAILURE,
    RESET_MFA_REQUEST,
    RESET_MFA_SUCCESS,
    RESET_MFA_FAILURE,
    RESET_MFA_TOKEN_REQUEST,
    RESET_MFA_TOKEN_SUCCESS,
    RESET_MFA_TOKEN_FAILURE,
    SECURE_QR_REQUEST,
    SECURE_QR_SUCCESS,
    SECURE_QR_FAILURE,
    ADMIN_RESET_PASSWORD_REQUEST,
    ADMIN_RESET_PASSWORD_SUCCESS,
    ADMIN_RESET_PASSWORD_FAILURE,
} from '../actions/adminAuthActions';

const initialState = {
    isAuthenticated: !!localStorage.getItem('admin_access_token'),
    accessToken: localStorage.getItem('admin_access_token') || null,
    refreshToken: localStorage.getItem('admin_refresh_token') || null,
    sessionId: localStorage.getItem('admin_session_id') || null,
    isLoading: false,
    error: null,
    errorCode: null,
    mfaRequired: false,
    mfaVerified: false,
    remainingBackupCodes: null,
    firstName: localStorage.getItem('admin_first_name') || null,
    lastName: localStorage.getItem('admin_last_name') || null,
    resetMfaToken: {
        secret: null,
        qrData: null,
        qrImage: null,
        remainingTime: null,
        isLoading: false,
        error: null,
    },
    secureQR: {
        secret: null,
        qrData: null,
        qrImage: null,
        remainingTime: null,
        isLoading: false,
        error: null,
    },
};

// Fonction utilitaire pour convertir les erreurs en chaîne
const formatError = (error) => {
    if (typeof error === 'string') {
        return error;
    }
    if (Array.isArray(error)) {
        // Si c'est un tableau d'erreurs de validation FastAPI
        return error.map(err => {
            if (typeof err === 'object' && err.msg) {
                // Extraire le chemin du champ (ignorer "body", "query", etc.)
                let loc = '';
                if (err.loc && Array.isArray(err.loc)) {
                    // Filtrer les éléments comme "body", "query", "path" et garder seulement le nom du champ
                    const fieldPath = err.loc.filter(part =>
                        part !== 'body' && part !== 'query' && part !== 'path' && part !== 'header'
                    );
                    loc = fieldPath.length > 0 ? fieldPath.join('.') : '';
                }

                // Traduire les messages d'erreur courants
                let msg = err.msg;
                if (msg === 'field required') {
                    msg = 'Ce champ est requis';
                } else if (msg === 'value is not a valid email') {
                    msg = 'Adresse email invalide';
                } else if (msg === 'value is not a valid integer') {
                    msg = 'Valeur entière invalide';
                } else if (msg === 'value is not a valid float') {
                    msg = 'Valeur numérique invalide';
                } else if (msg.includes('string does not match')) {
                    msg = 'Format invalide';
                }

                return loc ? `${loc}: ${msg}` : msg;
            }
            return String(err);
        }).join(', ');
    }
    if (typeof error === 'object' && error !== null) {
        if (error.msg) {
            let loc = '';
            if (error.loc && Array.isArray(error.loc)) {
                const fieldPath = error.loc.filter(part =>
                    part !== 'body' && part !== 'query' && part !== 'path' && part !== 'header'
                );
                loc = fieldPath.length > 0 ? fieldPath.join('.') : '';
            }
            let msg = error.msg;
            if (msg === 'field required') {
                msg = 'Ce champ est requis';
            }
            return loc ? `${loc}: ${msg}` : msg;
        }
        if (error.detail) {
            return formatError(error.detail);
        }
        if (error.message) {
            return error.message;
        }
        return JSON.stringify(error);
    }
    return String(error || 'Une erreur est survenue');
};

const adminAuthReducer = (state = initialState, action) => {
    switch (action.type) {
        // Login
        case ADMIN_LOGIN_REQUEST:
            return {
                ...state,
                isLoading: true,
                error: null,
                errorCode: null,
                mfaRequired: false,
                mfaVerified: false,
            };
        case ADMIN_LOGIN_SUCCESS:
            const loginResponse = action.payload;
            // Set token in API service
            if (loginResponse.access_token) {
                localStorage.setItem('admin_access_token', loginResponse.access_token);
                const { setAdminAuthToken } = require('../../services/api');
                setAdminAuthToken(loginResponse.access_token);
            }
            if (loginResponse.refresh_token) {
                localStorage.setItem('admin_refresh_token', loginResponse.refresh_token);
            }
            if (loginResponse.session_id) {
                localStorage.setItem('admin_session_id', loginResponse.session_id);
            }

            return {
                ...state,
                isLoading: false,
                error: null,
                accessToken: loginResponse.access_token || null,
                refreshToken: loginResponse.refresh_token || null,
                mfaVerified: loginResponse.mfa_verified || false,
                remainingBackupCodes: loginResponse.remaining_backup_codes || null,
                sessionId: loginResponse.session_id || state.sessionId,
                mfaRequired: !loginResponse.mfa_verified && !!loginResponse.session_id,
                isAuthenticated: loginResponse.mfa_verified || !loginResponse.session_id,
            };
        case ADMIN_LOGIN_FAILURE:
            return {
                ...state,
                isLoading: false,
                error: formatError(action.payload.detail || action.payload),
                errorCode: action.payload.error_code || null,
                isAuthenticated: false,
                mfaRequired: false,
                mfaVerified: false,
                accessToken: null,
                refreshToken: null,
                sessionId: null,
            };

        // MFA Verification
        case VERIFY_MFA_REQUEST:
            return {
                ...state,
                isLoading: true,
                error: null,
            };
        case VERIFY_MFA_SUCCESS:
            const mfaResponse = action.payload;
            if (mfaResponse.access_token) {
                localStorage.setItem('admin_access_token', mfaResponse.access_token);
                const { setAdminAuthToken } = require('../../services/api');
                setAdminAuthToken(mfaResponse.access_token);
            }
            if (mfaResponse.refresh_token) {
                localStorage.setItem('admin_refresh_token', mfaResponse.refresh_token);
            }
            if (mfaResponse.first_name) {
                localStorage.setItem('admin_first_name', mfaResponse.first_name);
            }
            if (mfaResponse.last_name) {
                localStorage.setItem('admin_last_name', mfaResponse.last_name);
            }

            return {
                ...state,
                isLoading: false,
                error: null,
                isAuthenticated: true,
                mfaRequired: false,
                mfaVerified: true,
                accessToken: mfaResponse.access_token || state.accessToken,
                refreshToken: mfaResponse.refresh_token || state.refreshToken,
                remainingBackupCodes: mfaResponse.remaining_backup_codes || state.remainingBackupCodes,
                firstName: mfaResponse.first_name || state.firstName,
                lastName: mfaResponse.last_name || state.lastName,
            };
        case VERIFY_MFA_FAILURE:
            return {
                ...state,
                isLoading: false,
                error: formatError(action.payload.detail || action.payload),
                errorCode: action.payload.error_code || null,
            };

        // Reset MFA
        case RESET_MFA_REQUEST:
            return {
                ...state,
                isLoading: true,
                error: null,
            };
        case RESET_MFA_SUCCESS:
            return {
                ...state,
                isLoading: false,
                error: null,
            };
        case RESET_MFA_FAILURE:
            return {
                ...state,
                isLoading: false,
                error: formatError(action.payload.detail || action.payload),
            };

        // Reset MFA Token
        case RESET_MFA_TOKEN_REQUEST:
            return {
                ...state,
                resetMfaToken: {
                    ...state.resetMfaToken,
                    isLoading: true,
                    error: null,
                },
            };
        case RESET_MFA_TOKEN_SUCCESS:
            return {
                ...state,
                resetMfaToken: {
                    secret: action.payload.secret,
                    qrData: action.payload.qr_data,
                    qrImage: action.payload.qr_image,
                    remainingTime: action.payload.remaining_time,
                    isLoading: false,
                    error: null,
                },
            };
        case RESET_MFA_TOKEN_FAILURE:
            return {
                ...state,
                resetMfaToken: {
                    ...state.resetMfaToken,
                    isLoading: false,
                    error: formatError(action.payload.detail || action.payload),
                },
            };

        // Secure QR
        case SECURE_QR_REQUEST:
            return {
                ...state,
                secureQR: {
                    ...state.secureQR,
                    isLoading: true,
                    error: null,
                },
            };
        case SECURE_QR_SUCCESS:
            return {
                ...state,
                secureQR: {
                    secret: action.payload.secret,
                    qrData: action.payload.qr_data,
                    qrImage: action.payload.qr_image,
                    remainingTime: action.payload.remaining_time,
                    isLoading: false,
                    error: null,
                },
            };
        case SECURE_QR_FAILURE:
            return {
                ...state,
                secureQR: {
                    ...state.secureQR,
                    isLoading: false,
                    error: formatError(action.payload.detail || action.payload),
                },
            };

        // Reset Password
        case ADMIN_RESET_PASSWORD_REQUEST:
            return {
                ...state,
                isLoading: true,
                error: null,
            };
        case ADMIN_RESET_PASSWORD_SUCCESS:
            return {
                ...state,
                isLoading: false,
                error: null,
            };
        case ADMIN_RESET_PASSWORD_FAILURE:
            return {
                ...state,
                isLoading: false,
                error: formatError(action.payload.detail || action.payload),
            };

        case 'CLEAR_ADMIN_AUTH_ERROR':
            return {
                ...state,
                error: null,
                errorCode: null,
            };

        case 'CLEAR_RESET_MFA_TOKEN':
            return {
                ...state,
                resetMfaToken: {
                    secret: null,
                    qrData: null,
                    qrImage: null,
                    remainingTime: null,
                    isLoading: false,
                    error: null,
                },
            };

        case 'CLEAR_SECURE_QR':
            return {
                ...state,
                secureQR: {
                    secret: null,
                    qrData: null,
                    qrImage: null,
                    remainingTime: null,
                    isLoading: false,
                    error: null,
                },
            };

        case 'ADMIN_LOGOUT':
            localStorage.removeItem('admin_access_token');
            localStorage.removeItem('admin_refresh_token');
            localStorage.removeItem('admin_session_id');
            localStorage.removeItem('admin_first_name');
            localStorage.removeItem('admin_last_name');
            const { setAdminAuthToken } = require('../../services/api');
            setAdminAuthToken(null);
            return {
                ...initialState,
            };

        default:
            return state;
    }
};

export default adminAuthReducer;

